import { PrismAd } from "./ad-models/PrismAd";
import { PrismAdBreak } from "./ad-models/PrismAdBreak";
import { TrackingData } from "./api/api-types";
import { Ad, AdBreak } from "./public-types";

type SessionData = {
  manifestUrl: string;
  trackingData: TrackingData[];
};

type Callbacks = {
  onAdBreakStart: (adBreak: AdBreak) => void;
  onAdBreakEnd: (adBreak: AdBreak) => void;
  onAdStart: (ad: Ad) => void;
  onAdEnd: (ad: Ad) => void;
};

export type PrismSessionOptions = {
  sessionData: SessionData;
  callbacks: Callbacks;
};

export class PrismSession {
  private sessionData: SessionData;

  private prismAdBreaks: PrismAdBreak[];

  private activePrismAdBreak?: PrismAdBreak;
  private activePrismAd?: PrismAd;

  constructor(options: PrismSessionOptions) {
    this.sessionData = options.sessionData;

    this.prismAdBreaks = this.sessionData.trackingData.map(
      (trackingData) =>
        new PrismAdBreak({
          trackingData,
          callbacks: options.callbacks,
        })
    );
  }

  public get manifestUrl() {
    return this.sessionData.manifestUrl;
  }

  public get adBreaks(): AdBreak[] {
    return this.prismAdBreaks.map((adBreak) => adBreak.publicAdBreak);
  }

  public get isInAdBreak() {
    return !!this.activePrismAdBreak;
  }

  public get activeAdBreak() {
    return this.activePrismAdBreak?.publicAdBreak;
  }

  public get activeAd() {
    return this.activePrismAd?.publicAd;
  }

  public handlePause() {
    this.activePrismAd?.handlePause();
  }

  public handleResume() {
    this.activePrismAd?.handleResume();
  }

  public handleStreamPositionUpdate(streamPosition: number) {
    const activeBreak = this.prismAdBreaks.find((adBreak) =>
      adBreak.isStreamPositionInAdBreak(streamPosition)
    );

    if (!this.activePrismAdBreak && activeBreak) {
      this.activePrismAdBreak = activeBreak;
      activeBreak.handleStart();
    }

    if (this.activePrismAdBreak && !activeBreak) {
      if (this.activePrismAd) {
        this.activePrismAd.handleEnd();
        this.activePrismAd = undefined;
      }

      this.activePrismAdBreak.handleEnd();
      this.activePrismAdBreak = undefined;
    }

    if (!activeBreak) {
      return;
    }

    const activeAd = activeBreak.getAdForStreamPosition(streamPosition);
    if (this.activePrismAd === activeAd) {
      this.activePrismAd?.handleStreamPositionUpdate(streamPosition);
      return;
    }
    this.activePrismAd?.handleEnd();
    this.activePrismAd = activeAd;
    this.activePrismAd?.handleStart();
  }

  public getStreamPositionForContentPosition(contentPosition: number) {
    let playheadPosition = contentPosition;

    const adBreaks = this.sessionData.trackingData;
    for (const adBreak of adBreaks) {
      if (playheadPosition >= adBreak.breakStart) {
        playheadPosition =
          playheadPosition + (adBreak.breakEnd - adBreak.breakStart);
      }
    }
    return playheadPosition;
  }

  public getContentPositionForStreamPosition(streamPosition: number) {
    const adBreaks = this.sessionData.trackingData;
    let contentPosition = streamPosition;
    for (const adBreak of adBreaks) {
      if (streamPosition >= adBreak.breakEnd) {
        contentPosition =
          contentPosition - (adBreak.breakEnd - adBreak.breakStart);
      }
    }
    return contentPosition;
  }

  public getAdBreakForStreamPosition(streamPosition: number) {
    return this.prismAdBreaks.find((adBreak) =>
      adBreak.isStreamPositionInAdBreak(streamPosition)
    )?.publicAdBreak;
  }

  public deactivateAdBreaksBefore(streamPosition: number) {
    for (const adBreak of this.prismAdBreaks) {
      if (adBreak.publicAdBreak.start < streamPosition) {
        adBreak.deactivate();
      }
    }
  }
}
